// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect } from 'react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarActions,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import type { ServiceHealthStatus } from '../hooks/useServiceHealth';

interface DeployBannerProps {
  status: ServiceHealthStatus;
}

/**
 * Shared banner displayed when the backend API is unavailable (likely due to a deploy).
 * Shows a warning when service is down and a success message when it recovers.
 * Dismissible — but reappears if status changes.
 *
 * Shared across all GMS agent web apps.
 */
export function DeployBanner({ status }: DeployBannerProps) {
  const intl = useIntl();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state when status changes
  useEffect(() => {
    setDismissed(false);
  }, [status]);

  if (status === 'healthy' || dismissed) return null;

  if (status === 'recovered') {
    return (
      <div className="deploy-banner">
        <MessageBar intent="success">
          <MessageBarBody>
            <Spinner size="tiny" className="deploy-banner-spinner" />
            {intl.formatMessage({ id: 'deploy.recovered' })}
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  // status === 'unavailable'
  return (
    <div className="deploy-banner">
      <MessageBar intent="warning">
        <MessageBarBody>
          {intl.formatMessage({ id: 'deploy.unavailable' })}
        </MessageBarBody>
        <MessageBarActions>
          <Button
            appearance="transparent"
            size="small"
            icon={<DismissRegular />}
            onClick={() => { setDismissed(true); }}
          />
        </MessageBarActions>
      </MessageBar>
    </div>
  );
}
